/**
 * promissoryMath.js — Muhasebe FAZ 4.8 senet iş kuralları (SAF fonksiyonlar).
 *
 * Bu dosya BİLEREK hiçbir şey import etmez (React, i18n, para formatlayıcı
 * dahil): tek sorumluluğu backend'in `services/promissory_note_service.py`
 * içindeki hesap/kural davranışını frontend önizlemesinde BİREBİR taklit
 * etmek. Böylece jest tarafında `@/` alias'ı, DOM, context vb. hiçbir mock
 * gerekmeden doğrudan test edilebiliyor (bkz. __tests__/accounting/
 * promissoryMath.test.js).
 *
 * Kapsanan backend kaynakları:
 *   - split_amounts()             (promissory_note_service.py:212)
 *   - _validate_due_date_or_400() (promissory_note_service.py:714)
 *   - _normalize_parts()          (promissory_note_service.py:444)
 *   - NOTE_STATUS_* durum makinesi (aynı dosya + routes/payments.py)
 */

// ── Senet statüleri ──────────────────────────────────────────────────────────
// Backend'in yazdığı TÜM değerler. `collecting`/`splitting`/`merging` ÇAPA
// (geçici) durumlardır: tahsilat/parçalama/birleştirme işlemi yarıda kalmış
// demektir — ekranda "muhasebe mutabakatı gerekli" olarak gösterilir ve TÜM
// aksiyonlar kapanır.
//
// `collecting` FAZ 4 PAKET I ile eklendi (Y-1): tahsilat yolunda CAS çapası.
// Zincir `active -> collecting -> collected`; para HİÇ yazılmadan hata
// alınırsa `collecting -> active` geri bırakılır
// (promissory_note_service.py::anchor_note_for_collection /
// release_note_collection_anchor). `collecting`'te TAKILI bir senet "tahsilat
// yarıda kaldı" demektir ve ikinci tahsilat denemesi 409
// `senet_tahsilati_devam_ediyor` alır — bu yüzden aksiyonlar kapatılır.
export const NOTE_STATUS_ACTIVE = 'active';
export const NOTE_STATUS_COLLECTING = 'collecting';
export const NOTE_STATUS_COLLECTED = 'collected';
export const NOTE_STATUS_CANCELLED = 'cancelled';
export const NOTE_STATUS_SPLIT = 'split';
export const NOTE_STATUS_MERGED = 'merged';
export const NOTE_STATUS_LEGAL = 'legal';
export const NOTE_STATUS_SPLITTING = 'splitting';
export const NOTE_STATUS_MERGING = 'merging';

/** Zincirin sonuna gelmiş (artık silinemeyen) statüler. */
export const TERMINAL_NOTE_STATUSES = [
  NOTE_STATUS_COLLECTED,
  NOTE_STATUS_CANCELLED,
  NOTE_STATUS_SPLIT,
  NOTE_STATUS_MERGED,
  NOTE_STATUS_LEGAL,
];

/** İşlem yarıda kalmış (çapa) statüler — muhasebe mutabakatı gerekir. */
export const STUCK_NOTE_STATUSES = [
  NOTE_STATUS_COLLECTING,
  NOTE_STATUS_SPLITTING,
  NOTE_STATUS_MERGING,
];

export function isActiveNote(status) {
  return status === NOTE_STATUS_ACTIVE;
}

export function isStuckNote(status) {
  return STUCK_NOTE_STATUSES.includes(status);
}

export function isTerminalNote(status) {
  return TERMINAL_NOTE_STATUSES.includes(status);
}

/**
 * Bir senedin statüsüne göre hangi aksiyon butonlarının GÖSTERİLECEĞİNİ ve
 * hangilerinin TIKLANABİLİR olduğunu döndürür.
 *
 * Backend sözleşmesi (routes/payments.py):
 *   DELETE  -> yalnız `active`, aksi 409 `senet_silinemez`
 *   /cancel -> yalnız `active`, aksi 409 `senet_durum_gecisi_gecersiz`
 *
 * UI kuralı (görev talimatı): terminal statüde "Sil" butonu GÖSTERİLMEZ,
 * yerine "İptal Et" gösterilir. Terminal senette iptal ucu da 409 döneceği
 * için buton görünür ama DEVRE DIŞI + gerekçe metniyle sunulur (kullanıcıya
 * kesin 409 yedirmemek için) — "silme yok, gerekçeli iptal var" mesajı
 * korunur, yanlış vaat verilmez.
 */
export function noteActionAvailability(status) {
  if (isStuckNote(status)) {
    // Çapa durum: hiçbir aksiyon açılmaz.
    return { stuck: true, showDelete: false, canDelete: false, showCancel: false, canCancel: false };
  }
  if (isActiveNote(status)) {
    return { stuck: false, showDelete: true, canDelete: true, showCancel: true, canCancel: true };
  }
  return { stuck: false, showDelete: false, canDelete: false, showCancel: true, canCancel: false };
}

// ── Yuvarlama: Python `round(x, 2)` birebir taklidi ──────────────────────────
/**
 * Python `round(x, 2)` davranışını taklit eder: eşitlik (tie) halinde ÇİFTE
 * yuvarlar (banker's rounding), diğer hallerde en yakına.
 *
 * Neden `Math.round(x * 100) / 100` DEĞİL: JS `Math.round` ve `toFixed`
 * eşitlik halinde YUKARI yuvarlar, Python `round` ise ÇİFTE. Örnek:
 * base=1000.25, n=2 -> 500.125 (ikili tabanda TAM temsil edilir) ->
 * Python 500.12, naif JS 500.13. Bu sapma önizlemede admin'e backend'in
 * yazmayacağı bir kuruş gösterirdi.
 *
 * Tie tespiti `toFixed(20)` üzerinden yapılır: bu büyüklükteki bir double'ın
 * tam ondalık açılımı 3. basamaktan sonra ancak 20 basamak içinde sıfırlanır
 * (aksi halde zaten tam eşitlik değildir).
 */
export function round2(value) {
  const x = Number(value);
  if (!Number.isFinite(x)) return NaN;
  const sign = x < 0 ? -1 : 1;
  const abs = Math.abs(x);
  const [intPart, frac = ''] = abs.toFixed(20).split('.');
  const cents = Number(intPart) * 100 + Number(frac.slice(0, 2).padEnd(2, '0'));
  const rest = frac.slice(2).replace(/0+$/, '');

  let roundUp = false;
  if (rest.length > 0) {
    if (rest[0] > '5') roundUp = true;
    else if (rest[0] === '5') {
      // Tam eşitlik ise çifte yuvarla (Python), değilse yukarı.
      roundUp = rest.length > 1 ? true : cents % 2 === 1;
    }
  }
  return (sign * (cents + (roundUp ? 1 : 0))) / 100;
}

// ── Parçalama önizlemesi ─────────────────────────────────────────────────────
export const SPLIT_MIN_PARTS = 2;
export const SPLIT_MAX_PARTS = 60;
/** INV-1 toleransı — backend `_EPS` ile aynı (promissory_note_service.py). */
export const AMOUNT_EPSILON = 0.005;

/**
 * Tutarı kuruş (integer cent) cinsine çevirir — backend `Decimal(str(base))
 * .quantize(0.01, ROUND_HALF_UP)`'ın tam sayı (cent) karşılığı.
 *
 * BİLEREK `round2()` KULLANILMAZ: `round2` Python `round()`'u (ikili taban
 * double DEĞERİ üzerinde banker's rounding) taklit eder — ama backend'in
 * split_amounts'ı `round()` DEĞİL, `Decimal(str(base))` kullanır (float'ın
 * kendisini değil, STRING TEMSİLİNİ ondalık olarak okur). İkisi FARKLI
 * sonuç verebilir: `2.675` double'da gerçekte `2.67499999999999982...`'dir;
 * Python `round(2.675, 2)` bu yüzden `2.67` verir (round2 de aynısını
 * taklit eder), ama `Decimal(str(2.675))` `"2.675"` string'ini TAM 2.675
 * olarak okur ve HALF_UP ile `2.68` verir. `toCents` bu yüzden JS
 * `Number.toString()`'in (Python `str()` gibi) ürettiği EN KISA round-trip
 * decimal string üzerinden HALF_UP yuvarlar — backend ile birebir aynı yolu
 * izler.
 */
function toCents(amount) {
  const num = Number(amount);
  if (!Number.isFinite(num)) return NaN;
  const sign = num < 0 ? -1 : 1;
  const str = Math.abs(num).toString();
  if (/e/i.test(str)) {
    // Bilimsel gösterim (aşırı büyük/küçük değer) — gerçekçi para
    // tutarlarında oluşmaz; savunma amaçlı düz yuvarlamaya düşer.
    return sign * Math.round(Math.abs(num) * 100);
  }
  const [intPart, fracRaw = ''] = str.split('.');
  const frac = fracRaw.padEnd(2, '0');
  const twoDecimals = frac.slice(0, 2);
  const rest = frac.slice(2);
  let cents = Number(intPart) * 100 + Number(twoDecimals);
  if (rest.length > 0 && rest[0] >= '5') cents += 1; // ROUND_HALF_UP
  return sign * cents;
}

/** Kuruş (integer cent) -> tutar; double drift'e karşı `toFixed(2)` ile döner. */
function centsToAmount(cents) {
  return Number((cents / 100).toFixed(2));
}

/**
 * Eşit bölme önizlemesi — backend `split_amounts()` ile BİREBİR aynı (B8
 * düzeltmesi):
 *   per = AŞAĞI yuvarlanmış (base / n); son parça = base - per * (n - 1)
 * Kuruş artığının TAMAMI SON parçaya gider VE son parça matematiksel olarak
 * DİĞERLERİNDEN KÜÇÜK OLAMAZ (per = floor(base/n) olduğundan
 * per*n <= base, dolayısıyla son parça = base - per*(n-1) >= per).
 * Örnek: 25.000 / 3 -> [8333.33, 8333.33, 8333.34]
 * Örnek: 8.333,33 / 3 -> [2777.77, 2777.77, 2777.79]
 *
 * Eski sürüm `per = round(amount / count)` (EN YAKINA) kullanıyordu —
 * `per` yukarı yuvarlanan bir kesre denk geldiğinde son parça diğerlerinden
 * KÜÇÜK çıkabiliyordu (kuralın tersi). Bölme integer cent üzerinde
 * (`Math.floor`) yapılır, double bölme yuvarlama sapmasından kaçınmak için.
 *
 * Geçersiz girdide (n < 2, n > 60, n tam sayı değil, base sonlu/pozitif değil)
 * BOŞ dizi döner — çağıran önizlemeyi gizler, uydurma rakam göstermez.
 */
export function splitPreviewAmounts(base, n) {
  const count = Number(n);
  const amount = Number(base);
  if (!Number.isInteger(count) || count < SPLIT_MIN_PARTS || count > SPLIT_MAX_PARTS) return [];
  if (!Number.isFinite(amount) || amount <= 0) return [];
  const totalCents = toCents(amount);
  const perCents = Math.floor(totalCents / count);
  const parts = new Array(count - 1).fill(centsToAmount(perCents));
  parts.push(centsToAmount(totalCents - perCents * (count - 1)));
  return parts;
}

/** Parça tutarları toplamı (2 hane yuvarlanmış). */
export function sumAmounts(amounts) {
  return round2((amounts || []).reduce((acc, v) => acc + (Number(v) || 0), 0));
}

/** Backend INV-1 kontrolü: |toplam - senet tutarı| <= 0.005 */
export function amountsMatchTotal(amounts, total) {
  return Math.abs(sumAmounts(amounts) - round2(total)) <= AMOUNT_EPSILON;
}

/**
 * Çocuk senet numarası önizlemesi — backend `_new_child_note_number()`:
 * ebeveyn numarası varsa `{ebeveyn}-{i}`, yoksa backend rastgele üretir
 * (önizlemede uydurmak yerine boş dönülür).
 */
export function childNoteNumberPreview(parentNoteNumber, index) {
  if (!parentNoteNumber) return '';
  return `${parentNoteNumber}-${index}`;
}

/** "SN-000123 (1/3)" — parçalanmış senet etiketi. */
export function splitLabel(noteNumber, splitIndex, splitTotal) {
  const base = noteNumber || '';
  if (!splitIndex || !splitTotal) return base;
  return base ? `${base} (${splitIndex}/${splitTotal})` : `(${splitIndex}/${splitTotal})`;
}

// ── Vade (S-10) ──────────────────────────────────────────────────────────────
/** "2027-01-01T00:00:00Z" / "2027-01-01" -> "2027-01-01"; boş girdi -> "" */
export function normalizeDueDate(value) {
  if (typeof value !== 'string') return '';
  return value.trim().slice(0, 10);
}

/** Bugünün UTC tarihi — backend `datetime.now(timezone.utc).date()` ile aynı. */
export function todayUtcDate() {
  return new Date().toISOString().slice(0, 10);
}

const DUE_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Backend `senet_birlestirme_vade_gecersiz` ile aynı biçim kontrolü. */
export function isValidDueDate(value) {
  const v = normalizeDueDate(value);
  if (!DUE_DATE_RE.test(v)) return false;
  const parsed = new Date(`${v}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === v;
}

/** Geçmiş tarih mi — backend `due_date_in_past` ile aynı (UTC, bugün hariç). */
export function isPastDueDate(value, today = todayUtcDate()) {
  const v = normalizeDueDate(value);
  if (!DUE_DATE_RE.test(v)) return false;
  return v < today;
}

/**
 * S-10 madde 2: birleştirme formunun VARSAYILAN vadesi = seçili senetlerin
 * `due_date` maksimumu (EN GEÇ vade). Sistem tarih HESAPLAMAZ/UYDURMAZ —
 * bu yalnızca admin'in değiştirebileceği bir ön dolgudur; hiçbir senette
 * geçerli vade yoksa boş döner ve alan zorunlu kalır.
 */
export function defaultMergeDueDate(notes) {
  return (notes || [])
    .map((n) => normalizeDueDate(n?.due_date))
    .filter((d) => DUE_DATE_RE.test(d))
    .reduce((max, d) => (d > max ? d : max), '');
}

/**
 * Birleştirme için seçilebilir senetler (backend INV-5'i ekranda ÖNCEDEN
 * uygular): aynı fatura + aynı öğrenci + hepsi `active`. `anchor` seçilen
 * ilk senettir; hiç seçim yoksa tüm `active` senetler aday olur.
 */
export function mergeEligibleNotes(notes, anchor) {
  const actives = (notes || []).filter((n) => isActiveNote(n?.status));
  if (!anchor) return actives;
  return actives.filter(
    (n) => n.invoice_id === anchor.invoice_id && n.user_id === anchor.user_id
  );
}

// ── S-14: senet ↔ taksit bağı (FAZ 4 PAKET H) ────────────────────────────────
/**
 * Borç TEK yerde durur: `installments`. Senet borcun kendisi değil, taksidin
 * HUKUKİ TEMSİLİDİR ve `installment_id` ile ona bağlanır. Aşağıdaki yardımcılar
 * backend'in POST /admin/promissory-notes doğrulama setini (routes/payments.py
 * "I-3: taksit bağı" bloğu) ekranda ÖNCEDEN uygular — admin 400/409 yemesin
 * diye aday listesi baştan kısıtlanır (NoteMergePanel'deki INV-5 deseniyle
 * aynı yaklaşım).
 */

/** Kapanmış taksit statüleri — backend `INSTALLMENT_TERMINAL_STATUSES`. */
export const INSTALLMENT_TERMINAL_STATUSES = ['paid', 'cancelled'];

/** Kapanmış taksit için senet oluşturulamaz (kapanan borç yeniden tahsil edilebilir hale gelirdi). */
export function isTerminalInstallment(status) {
  return INSTALLMENT_TERMINAL_STATUSES.includes(status);
}

/**
 * Takside gecikme faizi tahakkuk etmiş mi? Backend `amount_with_interest`
 * anaparadan 0.005'ten fazla büyükse 400 `senet_taksit_faiz_tahakkuk_etmis`
 * döner (senet tutarı anaparaya eşit olacağı için faiz sessizce silinirdi).
 */
export function installmentHasAccruedInterest(inst) {
  const amount = round2(inst?.amount || 0);
  const withInterest = round2(inst?.amount_with_interest || 0);
  return withInterest - amount > AMOUNT_EPSILON;
}

/**
 * BİREBİRLİK: bir taksidi en fazla BİR `active`/`legal` senet temsil eder.
 * Terminal statüdeki (collected/cancelled/split/merged) eski senetler aynı
 * `installment_id`'yi meşru şekilde taşıyabilir — onlar bağı TUTMAZ.
 */
// NOT: `collecting` BİLEREK YOK — backend'in birebirlik sorgusu da
// {"status": {"$in": [active, legal]}} (routes/payments.py "I-3" bloğu).
// Frontend aday listesini backend'den DAHA DAR tutarsa meşru bir senet
// oluşturma ekranda sessizce kaybolurdu.
export const INSTALLMENT_HOLDING_NOTE_STATUSES = [NOTE_STATUS_ACTIVE, NOTE_STATUS_LEGAL];

export function heldInstallmentIds(notes) {
  return (notes || [])
    .filter((n) => INSTALLMENT_HOLDING_NOTE_STATUSES.includes(n?.status))
    .map((n) => n?.installment_id)
    .filter(Boolean);
}

/**
 * Taksit neden senede bağlanamıyor? Bağlanabiliyorsa '' döner.
 * Dönen kodlar: 'terminal' | 'held' | 'interest'
 */
export function installmentLinkBlockReason(inst, heldIds = []) {
  if (isTerminalInstallment(inst?.status)) return 'terminal';
  if (heldIds.includes(inst?.installment_id)) return 'held';
  if (installmentHasAccruedInterest(inst)) return 'interest';
  return '';
}

/**
 * Senede bağlanabilir taksitler: AYNI fatura + AYNI öğrenci, terminal değil,
 * başka aktif/icra senedi tarafından tutulmuyor ve faiz tahakkuk etmemiş.
 * `invoiceId` boşken (fatura henüz seçilmemiş) BOŞ dizi döner — taksitler
 * faturadan bağımsız listelenirse admin kaçınılmaz olarak
 * `senet_taksit_faturasi_uyusmuyor` alırdı.
 */
export function linkableInstallments(installments, notes, invoiceId, userId) {
  if (!invoiceId) return [];
  const held = heldInstallmentIds(notes);
  return (installments || []).filter((inst) => {
    if (inst?.invoice_id !== invoiceId) return false;
    if (userId && inst?.user_id && inst.user_id !== userId) return false;
    return installmentLinkBlockReason(inst, held) === '';
  });
}

/**
 * Yalnızca "faiz tahakkuk etmiş" gerekçesiyle listeden düşen taksitler.
 * Ekranda sessizce yok sayılmaz; sayısı bilgi olarak gösterilir (terminal ve
 * zaten bağlı taksitler ise beklenen kapsam dışılık, ayrıca duyurulmaz).
 */
export function interestBlockedInstallments(installments, notes, invoiceId, userId) {
  if (!invoiceId) return [];
  const held = heldInstallmentIds(notes);
  return (installments || []).filter((inst) => {
    if (inst?.invoice_id !== invoiceId) return false;
    if (userId && inst?.user_id && inst.user_id !== userId) return false;
    return installmentLinkBlockReason(inst, held) === 'interest';
  });
}
